import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, Clock, CheckCircle, XCircle, Loader2, Search, Trash2, Eye, Phone, User, FileText, RefreshCw, Mail, Tag, Inbox } from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new: { label: 'جديد', labelEn: 'New', color: 'bg-blue-500', icon: Clock },
  in_progress: { label: 'قيد المعالجة', labelEn: 'In Progress', color: 'bg-amber-500', icon: Loader2 },
  resolved: { label: 'تم الحل', labelEn: 'Resolved', color: 'bg-emerald-500', icon: CheckCircle },
  closed: { label: 'مغلق', labelEn: 'Closed', color: 'bg-gray-500', icon: XCircle },
};

const PLATFORM_CONFIG = {
  website: { label: 'الموقع الرئيسي', labelEn: 'Website', color: 'bg-purple-500' },
  admin: { label: 'منصة الأدمن', labelEn: 'Admin Platform', color: 'bg-indigo-500' },
  merchants: { label: 'منصة التجار', labelEn: 'Merchants', color: 'bg-emerald-500' },
  customers: { label: 'منصة المتعاملين', labelEn: 'Customers', color: 'bg-blue-500' },
};

export default function ContactRequests() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, resolved: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modal states
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const fetchRequests = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (platformFilter !== 'all') params.append('platform', platformFilter);
      
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/contact-requests?${params.toString()}`,
        { headers: getHeaders() }
      );
      
      setRequests(res.data.data || []);
      setTotalPages(res.data.total_pages || 1);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching requests:', err);
      toast.error(language === 'ar' ? 'فشل في جلب الطلبات' : 'Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/contact-requests/stats`,
        { headers: getHeaders() }
      );
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchRequests();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchRequests(1);
  }, [statusFilter, platformFilter]);

  const updateStatus = async (requestId, newStatus) => {
    setUpdating(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/contact-requests/${requestId}/status`,
        { status: newStatus },
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم تحديث الحالة' : 'Status updated');
      fetchRequests(currentPage);
      fetchStats();
      setShowDetailsModal(false);
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error(language === 'ar' ? 'فشل في تحديث الحالة' : 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const deleteRequest = async (requestId) => {
    setDeleting(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/contact-requests/${requestId}`,
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم حذف الطلب' : 'Request deleted');
      fetchRequests(currentPage);
      fetchStats();
      setShowDeleteConfirm(false);
      setShowDetailsModal(false);
    } catch (err) {
      console.error('Error deleting request:', err);
      toast.error(language === 'ar' ? 'فشل في حذف الطلب' : 'Failed to delete request');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRequests = requests.filter(req => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      req.name?.toLowerCase().includes(query) ||
      req.email?.toLowerCase().includes(query) ||
      req.phone?.includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-emerald-500/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <DashboardHeader
        title={language === 'ar' ? 'فورم التواصل معنا' : 'Contact Us Form'}
        subtitle={language === 'ar' ? 'إدارة طلبات التواصل من الفورم' : 'Manage contact form requests'}
        icon={Inbox}
        iconColor="#10B981"
        showBackButton={true}
        backPath="/support-center"
      />

      {/* Main Content */}
      <main className="relative z-10">
        {/* Breadcrumb */}
        <div className="border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <BreadcrumbNav items={[
              { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
              { label_ar: 'الدعم والمساعدة', label_en: 'Support & Help', path: '/dashboard' },
              { label_ar: 'ASB606 ADMIN (الدعم)', label_en: 'ASB606 ADMIN (Support)', path: '/support-center' },
              { label_ar: 'فورم التواصل معنا', label_en: 'Contact Us Form', path: null }
            ]} />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
          >
            <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10">
              <p className="text-[#71717A] text-sm mb-1">{language === 'ar' ? 'الإجمالي' : 'Total'}</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
            <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-blue-500/30">
              <p className="text-blue-400 text-sm mb-1">{language === 'ar' ? 'جديد' : 'New'}</p>
              <p className="text-2xl font-bold text-blue-400">{stats.new}</p>
            </div>
            <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-amber-500/30">
              <p className="text-amber-400 text-sm mb-1">{language === 'ar' ? 'قيد المعالجة' : 'In Progress'}</p>
              <p className="text-2xl font-bold text-amber-400">{stats.in_progress}</p>
            </div>
            <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-emerald-500/30">
              <p className="text-emerald-400 text-sm mb-1">{language === 'ar' ? 'تم الحل' : 'Resolved'}</p>
              <p className="text-2xl font-bold text-emerald-400">{stats.resolved}</p>
            </div>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10 mb-6"
          >
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={language === 'ar' ? 'بحث بالاسم أو الهاتف...' : 'Search by name or phone...'}
                    className="bg-white/5 border-white/10 text-white pe-10 placeholder:text-[#71717A]"
                    data-testid="contact-search-input"
                  />
                </div>
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2 text-sm"
                data-testid="contact-status-filter"
              >
                <option value="all">{language === 'ar' ? 'كل الحالات' : 'All Status'}</option>
                <option value="new">{language === 'ar' ? 'جديد' : 'New'}</option>
                <option value="in_progress">{language === 'ar' ? 'قيد المعالجة' : 'In Progress'}</option>
                <option value="resolved">{language === 'ar' ? 'تم الحل' : 'Resolved'}</option>
              </select>

              <select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                className="bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2 text-sm"
                data-testid="contact-platform-filter"
              >
                <option value="all">{language === 'ar' ? 'كل المنصات' : 'All Platforms'}</option>
                <option value="website">{language === 'ar' ? 'الموقع الرئيسي' : 'Website'}</option>
                <option value="admin">{language === 'ar' ? 'منصة الأدمن' : 'Admin'}</option>
              </select>

              <Button
                onClick={() => { fetchRequests(); fetchStats(); }}
                variant="outline"
                size="sm"
                className="border-white/10 text-white hover:bg-white/5"
                data-testid="contact-refresh-btn"
              >
                <RefreshCw className="h-4 w-4 me-2" />
                {language === 'ar' ? 'تحديث' : 'Refresh'}
              </Button>
            </div>
          </motion.div>

          {/* Requests List */}
          {loading ? (
            <div className="text-center py-16 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mx-auto" />
              <p className="text-[#71717A] mt-4">{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-16 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10">
              <Inbox className="h-16 w-16 text-[#71717A] mx-auto mb-4 opacity-30" />
              <p className="text-[#71717A]">{language === 'ar' ? 'لا توجد طلبات' : 'No requests found'}</p>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-4"
            >
              {filteredRequests.map((request, index) => {
                const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.new;
                const platformConfig = PLATFORM_CONFIG[request.platform] || PLATFORM_CONFIG.website;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-5 border border-white/10 hover:border-emerald-500/30 transition-all cursor-pointer"
                    onClick={() => { setSelectedRequest(request); setShowDetailsModal(true); }}
                    data-testid={`contact-request-${request.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs text-white ${statusConfig.color}`}>
                            {language === 'ar' ? statusConfig.label : statusConfig.labelEn}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs text-white ${platformConfig.color}`}>
                            {language === 'ar' ? platformConfig.label : platformConfig.labelEn}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-white mb-1">
                          <User className="h-4 w-4 text-[#71717A]" />
                          <span className="font-medium">{request.name}</span>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-[#71717A]">
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            <span>{request.email}</span>
                          </div>
                          {request.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              <span dir="ltr">{request.phone}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 mt-2">
                          <Tag className="h-4 w-4 text-emerald-400" />
                          <span className="text-white">{request.title}</span>
                        </div>
                        
                        <p className="text-[#71717A] text-sm mt-2 line-clamp-2">{request.subject}</p>
                      </div>
                      
                      <div className="text-left text-sm text-[#71717A]">
                        <p>{formatDate(request.created_at)}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                          onClick={(e) => { e.stopPropagation(); setSelectedRequest(request); setShowDetailsModal(true); }}
                        >
                          <Eye className="h-4 w-4 me-1" />
                          {language === 'ar' ? 'عرض' : 'View'}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => fetchRequests(page)}
                  className={`w-10 h-10 rounded-lg font-medium transition-all ${
                    currentPage === page 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-white/5 text-[#71717A] hover:bg-white/10'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {language === 'ar' ? 'تفاصيل الطلب' : 'Request Details'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-sm text-white ${STATUS_CONFIG[selectedRequest.status]?.color || 'bg-gray-500'}`}>
                  {language === 'ar' ? STATUS_CONFIG[selectedRequest.status]?.label : STATUS_CONFIG[selectedRequest.status]?.labelEn}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm text-white ${PLATFORM_CONFIG[selectedRequest.platform]?.color || 'bg-gray-500'}`}>
                  {language === 'ar' ? PLATFORM_CONFIG[selectedRequest.platform]?.label : PLATFORM_CONFIG[selectedRequest.platform]?.labelEn}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#71717A] text-sm">{language === 'ar' ? 'الاسم' : 'Name'}</p>
                  <p className="text-white font-medium">{selectedRequest.name}</p>
                </div>
                <div>
                  <p className="text-[#71717A] text-sm">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</p>
                  <p className="text-white font-medium">{selectedRequest.email}</p>
                </div>
                {selectedRequest.phone && (
                  <div>
                    <p className="text-[#71717A] text-sm">{language === 'ar' ? 'الهاتف' : 'Phone'}</p>
                    <p className="text-white font-medium" dir="ltr">{selectedRequest.phone}</p>
                  </div>
                )}
                <div>
                  <p className="text-[#71717A] text-sm">{language === 'ar' ? 'التاريخ' : 'Date'}</p>
                  <p className="text-white font-medium">{formatDate(selectedRequest.created_at)}</p>
                </div>
              </div>
              
              <div>
                <p className="text-[#71717A] text-sm mb-1">{language === 'ar' ? 'العنوان' : 'Title'}</p>
                <p className="text-white font-medium">{selectedRequest.title}</p>
              </div>
              
              <div>
                <p className="text-[#71717A] text-sm mb-1">{language === 'ar' ? 'الرسالة' : 'Message'}</p>
                <div className="bg-white/5 rounded-xl p-4 text-white whitespace-pre-wrap">
                  {selectedRequest.subject}
                </div>
              </div>
              
              <div className="border-t border-white/10 pt-4">
                <p className="text-[#71717A] text-sm mb-2">{language === 'ar' ? 'تغيير الحالة' : 'Change Status'}</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => updateStatus(selectedRequest.id, key)}
                      disabled={updating || selectedRequest.status === key}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedRequest.status === key 
                          ? `${config.color} text-white` 
                          : 'bg-white/5 text-[#71717A] hover:bg-white/10'
                      }`}
                    >
                      {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'ar' ? config.label : config.labelEn)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30"
            >
              <Trash2 className="h-4 w-4 me-2" />
              {language === 'ar' ? 'حذف' : 'Delete'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowDetailsModal(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              {language === 'ar' ? 'إغلاق' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400">
              {language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#71717A]">
            {language === 'ar' 
              ? 'هل أنت متأكد من حذف هذا الطلب؟ لا يمكن التراجع عن هذا الإجراء.' 
              : 'Are you sure you want to delete this request? This action cannot be undone.'}
          </p>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedRequest && deleteRequest(selectedRequest.id)}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Trash2 className="h-4 w-4 me-2" />}
              {language === 'ar' ? 'حذف' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
